/**
 * Visual Discount Component
 * 
 * A web component that handles client-side date validation for visual discounts.
 * Shows/hides discount based on start/end date ranges.
 * 
 * @extends {HTMLElement}
 */
class VisualDiscountComponent extends HTMLElement {
  static get observedAttributes() {
    return ['data-product-id'];
  }

  connectedCallback() {
    this.init();
  }

  /**
   * @param {string} name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    // Re-init when product-id changes (morph updates this attribute)
    // Use setTimeout to run AFTER morph finishes updating children
    if (oldValue !== null && oldValue !== newValue) {
      setTimeout(() => this.init(), 0);
    }
  }

  /**
   * Initialize the component (can be called on reconnect)
   */
  init() {
    // Get date validation data
    const hasDates = this.dataset.hasDates === 'true';
    
    // If no dates, discount is always active - ensure data-active is set
    if (!hasDates) {
      const content = this.querySelector('[data-discount-content]');
      if (content) {
        content.setAttribute('data-active', 'true');
      }
      return;
    }

    // Parse dates from Unix timestamps (seconds)
    const startDate = this.dataset.startDate ? parseInt(this.dataset.startDate) * 1000 : null;
    const endDate = this.dataset.endDate ? parseInt(this.dataset.endDate) * 1000 : null;

    // Validate dates and show/hide accordingly
    this.validateAndDisplay(startDate, endDate);
  }

  /**
   * Validates current time against date range
   * @param {number|null} startDate - Start date timestamp in ms (null = no start constraint)
   * @param {number|null} endDate - End date timestamp in ms (null = no end constraint)
   */
  validateAndDisplay(startDate, endDate) {
    const now = Date.now();
    let isValid = true;

    // Check start date (if provided)
    if (startDate && now < startDate) {
      isValid = false;
    }

    // Check end date (if provided)
    if (endDate && now > endDate) {
      isValid = false;
    }

    // Show discount or fallback based on validation
    if (isValid) {
      this.showDiscount();
    } else {
      this.showFallback();
    }
  }

  /**
   * Shows the discount and hides fallback
   */
  showDiscount() {
    const content = this.querySelector('[data-discount-content]');
    const fallback = this.querySelector('[data-discount-fallback]');
    
    if (content) {
      content.setAttribute('data-active', 'true');
      content.removeAttribute('hidden');
    }
    
    if (fallback) {
      fallback.setAttribute('hidden', '');
    }
  }

  /**
   * Hides discount and shows fallback price
   */
  showFallback() {
    const content = this.querySelector('[data-discount-content]');
    const fallback = this.querySelector('[data-discount-fallback]');
    
    if (content) {
      content.removeAttribute('data-active');
      content.setAttribute('hidden', '');
    }
    
    if (fallback) {
      fallback.removeAttribute('hidden');
    }
  }
}

// Register custom element (with check to prevent duplicate registration)
if (!customElements.get('visual-discount-component')) {
  customElements.define('visual-discount-component', VisualDiscountComponent);
}

/**
 * Visual Discount Countdown Component
 * 
 * A web component that handles countdown timer display for visual discounts.
 * Updates every second and replaces [[timer]] placeholder with formatted time.
 * 
 * @extends {HTMLElement}
 */
class VisualDiscountCountdownComponent extends HTMLElement {
  static get observedAttributes() {
    return ['data-end-date'];
  }

  /** @type {number | null} */
  intervalId = null;

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    // Clean up interval when element is removed
    this.destroy();
  }

  /**
   * @param {string} name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    // Re-init when end-date changes (morph updates this attribute)
    if (oldValue !== null && oldValue !== newValue) {
      this.destroy();
      setTimeout(() => this.init(), 0);
    }
  }

  /**
   * Initialize the countdown timer
   */
  init() {
    const endDateStr = this.dataset.endDate;
    const countdownText = this.dataset.countdownText;
    const displayEl = this.querySelector('[data-countdown-display]');

    // Guard against missing data
    if (!endDateStr || !countdownText || !displayEl) return;

    const endDateUnix = parseInt(endDateStr, 10);
    if (isNaN(endDateUnix)) return;

    this.textTemplate = countdownText;
    this.displayEl = displayEl;
    this.endDate = endDateUnix * 1000; // Convert to milliseconds

    // Initial update
    this.updateCountdown();

    // Set interval for updates
    this.intervalId = setInterval(() => {
      const shouldContinue = this.updateCountdown();
      if (!shouldContinue) {
        this.destroy();
      }
    }, 1000);
  }

  /**
   * Updates the countdown display
   * @returns {boolean} Whether to continue the countdown
   */
  updateCountdown() {
    // Guard against uninitialized state
    if (!this.endDate || !this.textTemplate || !this.displayEl) return false;

    const now = Date.now();
    const diff = this.endDate - now;

    // If countdown is over, hide the component
    if (diff <= 0) {
      this.style.display = 'none';
      return false;
    }

    // Calculate time components
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    // Format the timer string
    let timerString = '';

    if (days > 0) {
      timerString += `${days}d `;
    }
    if (hours > 0 || days > 0) {
      timerString += `${hours}h `;
    }
    if (minutes > 0 || hours > 0 || days > 0) {
      timerString += `${minutes}m `;
    }
    timerString += `${seconds}s`;

    // Replace placeholder and update display
    const finalText = this.textTemplate.replace('[[timer]]', timerString.trim());
    this.displayEl.textContent = finalText;

    return true;
  }

  /**
   * Clean up the interval timer
   */
  destroy() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }
}

// Register custom element (with check to prevent duplicate registration)
if (!customElements.get('visual-discount-countdown')) {
  customElements.define('visual-discount-countdown', VisualDiscountCountdownComponent);
}

